// src/views/Inventory/IncomingPC/DetailIncomingPC.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  Select,
  Input,
  DatePicker,
  Button,
  Typography,
  Space,
  Divider,
  message,
  Modal,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  RollbackOutlined,
  UploadOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import axiosInstance from "../../../configs";
import Title from "antd/es/typography/Title";
import { canEdit, MODULES } from "../../../utils/rbac";

const { Text } = Typography;
const { Option } = Select;

export default function DetailIncomingPC() {
  const [form] = Form.useForm();
  const { idIncomingPc } = useParams(); // pastikan route: /inventory/incoming-pc/:idIncomingPc

  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  const [headerData, setHeaderData] = useState(null);

  const [uploadName, setUploadName] = useState(null);
  const [uploadBase64, setUploadBase64] = useState(null);

  const [loadingPage, setLoadingPage] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [originalFormValues, setOriginalFormValues] = useState(null);

  // Confirmation Modal State
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  const readOnly = !isEditing;

  // ---------- Load contacts (importir) ----------
  const fetchContacts = async () => {
    setLoadingContacts(true);
    try {
      const res = await axiosInstance.get("/contact", {
        params: { contact_type: 1 },
      });
      setContacts(res.data || []);
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat daftar importir");
    } finally {
      setLoadingContacts(false);
    }
  };

  // ---------- Load detail incoming-pc ----------
  const fetchDetailIncomingPc = async () => {
    if (!idIncomingPc) return;
    setLoadingPage(true);
    try {
      const res = await axiosInstance.get(
        `/incoming-pc/${idIncomingPc}/with-details`,
      );

      const header = res.data?.header || res.data?.data?.header || {};
      const details = res.data?.details || res.data?.data?.details || [];

      setHeaderData(header);

      // siapkan nilai form
      const formVals = {
        id_customer: header.id_customer || undefined,
        no_ck1a: header.no_ck1a || "",
        tanggal_terima: header.tanggal_terima
          ? dayjs(header.tanggal_terima)
          : null,
        jenis_bkc: header.jenis_bkc || "",
        // stpc_number & stpc_date hanya display (non editable)
        details: details.map((d) => ({
          id_incoming_pc_detail: d.id_incoming_pc_detail || 0,
          golongan: d.golongan,
          isi: d.isi,
          satuan: d.satuan,
          lembar: d.lembar,
          keping: d.keping,
          created_at: d.created_at,
          created_by: d.created_by,
        })),
      };

      form.setFieldsValue(formVals);
      setOriginalFormValues(formVals);

      // lampiran display
      if (header.lampiran_1) {
        setUploadName("Lampiran sudah tersimpan di server");
      }
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat data STPC");
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    fetchDetailIncomingPc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idIncomingPc]);

  // ---------- Watch detail untuk hitung total ----------
  const details = Form.useWatch("details", form);

  const { totalLembar, totalKeping } = useMemo(() => {
    const rows = details || [];
    let tL = 0;
    let tK = 0;
    rows.forEach((r) => {
      const lembar = Number(r?.lembar) || 0;
      const keping = Number(r?.keping) || 0;
      tL += lembar;
      tK += keping;
    });
    return { totalLembar: tL, totalKeping: tK };
  }, [details]);

  // ---------- Upload lampiran (saat edit) ----------
  const handleUploadChange = (e) => {
    if (readOnly) return;
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "application/pdf",
    ];
    const maxSize = 5 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      message.error("Jenis file harus JPG, PNG, GIF, atau PDF");
      return;
    }
    if (file.size > maxSize) {
      message.error("Ukuran file maksimal 5 MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      const base64Content = base64String.split(",")[1];
      setUploadName(file.name);
      setUploadBase64(base64Content);
    };
    reader.readAsDataURL(file);
  };

  // ---------- Edit toggle ----------
  const handleStartEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    if (originalFormValues) {
      form.setFieldsValue(originalFormValues);
    }
    setIsEditing(false);
  };

  const handleBack = () => {
    window.history.back();
  };

  // ---------- Submit (update) ----------
  const handleFinish = async (values) => {
    if (!headerData || !headerData.id_incoming_pc) {
      message.error("Data STPC tidak valid");
      return;
    }

    if (!details || details.length === 0) {
      message.error("Minimal 1 baris detail wajib diisi");
      return;
    }

    setPendingValues(values);
    setConfirmVisible(true);
  };

  const handleConfirmSubmit = async () => {
    const values = pendingValues;

    setSaving(true);
    try {
      const nowIso = new Date().toISOString();

      const payload = {
        header: {
          id_incoming_pc: headerData.id_incoming_pc,
          id_customer: values.id_customer,
          no_ck1a: values.no_ck1a,
          tanggal_terima: values.tanggal_terima
            ? dayjs(values.tanggal_terima).format("YYYY-MM-DD")
            : null,
          stpc_number: headerData.stpc_number,
          stpc_date: headerData.stpc_date
            ? dayjs(headerData.stpc_date).format("YYYY-MM-DD")
            : null,
          // backend awalnya pakai jenis_pita, jadi kita kirim dari jenis_cukai
          jenis_bkc: values.jenis_bkc,
          total_lembar: totalLembar,
          total_keping: totalKeping,
          lampiran_1:
            uploadBase64 != null ? uploadBase64 : headerData.lampiran_1 || null,
          created_by: headerData.created_by || 0,
          created_at: headerData.created_at
            ? dayjs(headerData.created_at).format("YYYY-MM-DD HH:mm:ss")
            : dayjs(nowIso).format("YYYY-MM-DD HH:mm:ss"),
          updated_by: 0,
          updated_at: dayjs(nowIso).format("YYYY-MM-DD HH:mm:ss"),
        },
        details: (values.details || []).map((row, idx) => ({
          id_incoming_pc_detail: row.id_incoming_pc_detail || 0,
          id_incoming_pc: headerData.id_incoming_pc,
          seri: idx + 1,
          golongan: row.golongan,
          isi: row.isi,
          satuan: row.satuan,
          lembar: Number(row.lembar) || 0,
          keping: Number(row.keping) || 0,
          created_by: row.created_by || 0,
          created_at: row.created_at
            ? dayjs(row.created_at).format("YYYY-MM-DD HH:mm:ss")
            : dayjs(nowIso).format("YYYY-MM-DD HH:mm:ss"),
          updated_by: 0,
          updated_at: dayjs(nowIso).format("YYYY-MM-DD HH:mm:ss"),
        })),
      };

      await axiosInstance.put(
        `/incoming-pc/${headerData.id_incoming_pc}/with-details`,
        payload,
      );

      message.success("Perubahan STPC berhasil disimpan");
      setIsEditing(false);
      message.success("Perubahan STPC berhasil disimpan");
      setIsEditing(false);
      setOriginalFormValues(values);
      setConfirmVisible(false);
    } catch (err) {
      console.error(err);
      message.error("Gagal menyimpan perubahan STPC");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
      {/* HEADER TITLE + ACTION */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 4 }}>
            Detail Serah Terima Pita Cukai
          </Title>
          <Text type="secondary">
            Lihat dan ubah data penerimaan pita cukai (STPC).
          </Text>
          {headerData && (
            <div style={{ marginTop: 8 }}>
              <Text strong>No STPC:&nbsp;</Text>
              <Text>{headerData.stpc_number}</Text>
              <br />
              <Text strong>Tanggal STPC:&nbsp;</Text>
              <Text>
                {headerData.stpc_date
                  ? dayjs(headerData.stpc_date).format("DD MMMM YYYY")
                  : "-"}
              </Text>
            </div>
          )}
        </Col>
        <Col>
          <Space>
            <Button onClick={handleBack}>Kembali</Button>
            {!isEditing && canEdit(MODULES.PITA_CUKAI) && (
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={handleStartEdit}
                disabled={loadingPage}
              >
                Edit
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        disabled={loadingPage}
      >
        {/* ===== HEADER ===== */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Nama Importir</Text>}
              name="id_customer"
              rules={[{ required: true, message: "Pilih nama importir" }]}
            >
              <Select
                placeholder="Pilih nama importir"
                loading={loadingContacts}
                showSearch
                optionFilterProp="children"
                disabled={readOnly}
              >
                {contacts.map((c) => (
                  <Option key={c.id_contact} value={c.npwp}>
                    {c.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>No CK1A</Text>}
              name="no_ck1a"
              rules={[{ required: true, message: "Isi No CK1A" }]}
            >
              <Input placeholder="Masukkan No CK1A" disabled={readOnly} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Tanggal Diterima</Text>}
              name="tanggal_terima"
              rules={[{ required: true, message: "Pilih tanggal diterima" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD MMMM YYYY"
                placeholder="Pilih tanggal"
                disabled={readOnly}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Jenis BKC</Text>}
              name="jenis_bkc"
              rules={[{ required: true, message: "Jenis BKC" }]}
            >
              <Input placeholder="Contoh: MMEA" disabled={readOnly} />
            </Form.Item>
          </Col>
        </Row>

        {/* ===== LAMPIRAN ===== */}
        <Divider style={{ margin: "8px 0 16px" }} />
        <Row gutter={16} align="middle">
          <Col xs={24} md={3}>
            <Text strong>Lampiran 1</Text>
          </Col>
          <Col xs={24} md={21}>
            <Space direction="vertical" size={4}>
              <Button
                icon={<UploadOutlined />}
                type="primary"
                disabled={readOnly}
                onClick={() => {
                  if (readOnly) return;
                  document
                    .getElementById("upload-stpc-lampiran-detail")
                    .click();
                }}
                style={{
                  background: readOnly ? "#E4E6EF" : "#FFA800",
                  borderColor: readOnly ? "#E4E6EF" : "#FFA800",
                  fontWeight: 600,
                }}
              >
                {readOnly ? "Lampiran" : "Ganti Lampiran"}
              </Button>
              <input
                id="upload-stpc-lampiran-detail"
                type="file"
                style={{ display: "none" }}
                accept=".jpg,.jpeg,.png,.gif,.pdf"
                onChange={handleUploadChange}
              />
              <Text type="secondary">
                Jenis File: JPG | JPEG | PNG | GIF | PDF. Maks 5 MB.
              </Text>
              {uploadName && <Text type="success">File: {uploadName}</Text>}
            </Space>
          </Col>
        </Row>

        {/* ===== TABEL DETAIL ===== */}
        <Divider style={{ margin: "16px 0" }} />

        <Card
          bodyStyle={{ padding: 0 }}
          style={{
            borderRadius: 4,
            border: "1px solid #E4E6EF",
            overflow: "hidden",
          }}
        >
          {/* header grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "0.3fr 2fr 2fr 0.7fr 0.7fr 1.2fr 80px",
              background: "#F3F6F9",
              padding: "8px 16px",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            <div>SERI</div>
            <div>GOLONGAN PITA</div>
            <div>ISI</div>
            <div>SATUAN</div>
            <div>LEMBAR</div>
            <div>KEPING</div>
            <div style={{ textAlign: "center" }}>AKSI</div>
          </div>

          <Form.List name="details">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <div key={field.key}>
                    {/* hidden id & audit */}
                    <Form.Item
                      {...field}
                      name={[field.name, "id_incoming_pc_detail"]}
                      fieldKey={[field.fieldKey, "id_incoming_pc_detail"]}
                      hidden
                    />
                    <Form.Item
                      {...field}
                      name={[field.name, "created_at"]}
                      fieldKey={[field.fieldKey, "created_at"]}
                      hidden
                    />
                    <Form.Item
                      {...field}
                      name={[field.name, "created_by"]}
                      fieldKey={[field.fieldKey, "created_by"]}
                      hidden
                    />

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "0.3fr 2fr 2fr 0.7fr 0.7fr 1.2fr 80px",
                        padding: "8px 16px",
                        borderTop: "1px solid #E4E6EF",
                        alignItems: "center",
                      }}
                    >
                      {/* SERI */}
                      <div style={{ paddingRight: 8 }}>{index + 1}</div>

                      {/* GOLONGAN */}
                      <Form.Item
                        {...field}
                        name={[field.name, "golongan"]}
                        fieldKey={[field.fieldKey, "golongan"]}
                        rules={[
                          { required: true, message: "Golongan wajib diisi" },
                        ]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input
                          placeholder="Golongan (misal: Pita Cukai A)"
                          disabled={readOnly}
                        />
                      </Form.Item>

                      {/* ISI */}
                      <Form.Item
                        {...field}
                        name={[field.name, "isi"]}
                        fieldKey={[field.fieldKey, "isi"]}
                        rules={[{ required: true, message: "Isi wajib diisi" }]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input
                          placeholder="Isi (misal: 700)"
                          disabled={readOnly}
                        />
                      </Form.Item>

                      {/* SATUAN */}
                      <Form.Item
                        {...field}
                        name={[field.name, "satuan"]}
                        fieldKey={[field.fieldKey, "satuan"]}
                        rules={[
                          { required: true, message: "Satuan wajib diisi" },
                          { max: 5, message: "Maksimal 5 huruf" },
                        ]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input
                          placeholder="ML"
                          maxLength={5}
                          disabled={readOnly}
                          style={{ textTransform: "uppercase" }}
                        />
                      </Form.Item>

                      {/* LEMBAR */}
                      <Form.Item
                        {...field}
                        name={[field.name, "lembar"]}
                        fieldKey={[field.fieldKey, "lembar"]}
                        rules={[
                          { required: true, message: "Isi jumlah lembar" },
                        ]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input
                          inputMode="numeric"
                          placeholder="0"
                          disabled={readOnly}
                          onChange={(e) => {
                            if (readOnly) return;
                            const v = e.target.value.replace(/[^0-9]/g, "");
                            form.setFieldsValue({
                              details: (
                                form.getFieldValue("details") || []
                              ).map((row, i) =>
                                i === index
                                  ? { ...row, lembar: v, keping: v * 60 }
                                  : row,
                              ),
                            });
                          }}
                        />
                      </Form.Item>

                      {/* KEPING */}
                      <Form.Item
                        {...field}
                        name={[field.name, "keping"]}
                        fieldKey={[field.fieldKey, "keping"]}
                        rules={[
                          { required: true, message: "Isi jumlah keping" },
                        ]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input
                          inputMode="numeric"
                          placeholder="0"
                          readOnly
                        />
                      </Form.Item>

                      <div style={{ textAlign: "center" }}>
                        {isEditing && fields.length > 1 && (
                          <Button
                            type="text"
                            icon={
                              <DeleteOutlined style={{ color: "#F64E60" }} />
                            }
                            onClick={() => remove(field.name)}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {isEditing && (
                  <div
                    style={{
                      padding: "8px 16px 12px",
                      borderTop: "1px solid #F3F6F9",
                    }}
                  >
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        add({
                          golongan: undefined,
                          isi: undefined,
                          satuan: undefined,
                          lembar: undefined,
                          keping: undefined,
                        })
                      }
                    >
                      Tambah Pita
                    </Button>
                  </div>
                )}
              </>
            )}
          </Form.List>
        </Card>

        {/* ===== TOTAL ===== */}
        <Row justify="end" style={{ marginTop: 16 }}>
          <Col>
            <div style={{ textAlign: "right" }}>
              <div>
                <Text strong>TOTAL LEMBAR :</Text>{" "}
                <Text>{totalLembar} Lembar</Text>
              </div>
              <div>
                <Text strong>TOTAL KEPING :</Text>{" "}
                <Text>{totalKeping} Keping</Text>
              </div>
            </div>
          </Col>
        </Row>

        {/* ===== ACTION BUTTONS ===== */}
        <Row justify="end" style={{ marginTop: 24 }}>
          <Space>
            <Button onClick={handleBack}>Kembali</Button>
            {isEditing && (
              <>
                <Button
                  icon={<RollbackOutlined />}
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  Batal Edit
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  icon={<SaveOutlined />}
                  style={{
                    background: "#1BC5BD",
                    borderColor: "#1BC5BD",
                    minWidth: 120,
                    fontWeight: 600,
                  }}
                >
                  Simpan Perubahan
                </Button>
              </>
            )}
          </Space>
        </Row>
      </Form>

      {/* Confirmation Modal */}
      <Modal
        title="Konfirmasi Perubahan Pita Cukai"
        open={confirmVisible}
        onOk={handleConfirmSubmit}
        onCancel={() => setConfirmVisible(false)}
        okText="Ya, Simpan Perubahan"
        cancelText="Batal"
        confirmLoading={saving}
        width={600}
      >
        {pendingValues && (
          <div>
            <p>Apakah Anda yakin ingin menyimpan perubahan data STPC ini?</p>
            <div
              style={{
                background: "#f5f5f5",
                padding: 10,
                borderRadius: 4,
                marginBottom: 16,
              }}
            >
              <p style={{ margin: 0 }}>
                <strong>No STPC:</strong> {headerData?.stpc_number}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Tanggal:</strong>{" "}
                {headerData?.stpc_date
                  ? dayjs(headerData.stpc_date).format("DD MMMM YYYY")
                  : "-"}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Importir:</strong>{" "}
                {contacts.find((c) => c.npwp === pendingValues.id_customer)
                  ?.name || pendingValues.id_customer}
              </p>
            </div>

            <Text strong>Ringkasan Pita:</Text>
            <div
              style={{
                maxHeight: 200,
                overflowY: "auto",
                marginTop: 8,
                border: "1px solid #f0f0f0",
              }}
            >
              <table
                style={{
                  width: "100%",
                  fontSize: 12,
                  borderCollapse: "collapse",
                }}
              >
                <thead
                  style={{ background: "#fafafa", position: "sticky", top: 0 }}
                >
                  <tr>
                    <th
                      style={{
                        padding: "4px 8px",
                        textAlign: "left",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Golongan
                    </th>
                    <th
                      style={{
                        padding: "4px 8px",
                        textAlign: "right",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Lembar
                    </th>
                    <th
                      style={{
                        padding: "4px 8px",
                        textAlign: "right",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Keping
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(pendingValues.details || []).map((d, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "4px 8px" }}>{d.golongan}</td>
                      <td style={{ padding: "4px 8px", textAlign: "right" }}>
                        {d.lembar}
                      </td>
                      <td style={{ padding: "4px 8px", textAlign: "right" }}>
                        {d.keping}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </Card>
  );
}
